import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { STORAGE_KEYS } from "@/shared/constants/storage-keys";
import { storage } from "@/shared/lib/storage";

import { cancelExportTask, createExportTask, getExportTask, type ExportTask } from "./api";

function readTaskIds() {
  const value = storage.getItem<Record<string, string>>(STORAGE_KEYS.exportTaskIds, {});
  return value && typeof value === "object" ? value : {};
}

function rememberTaskId(userId: string, taskId: string) {
  storage.setItem(STORAGE_KEYS.exportTaskIds, { ...readTaskIds(), [userId]: taskId });
}

export function useDataExport(userId: string, onUnlockPrivacy: () => Promise<boolean>) {
  const queryClient = useQueryClient();
  const [taskId, setTaskId] = useState<string | null>(() => readTaskIds()[userId] ?? null);
  const [isDownloading, setIsDownloading] = useState(false);
  const queryKey = ["data-export", userId, taskId] as const;

  const taskQuery = useQuery({
    queryKey,
    queryFn: () => {
      if (!taskId) throw new Error("缺少导出任务 ID");
      return getExportTask(taskId);
    },
    enabled: Boolean(taskId),
    refetchOnMount: "always",
    refetchInterval: (query) =>
      query.state.data?.status === "queued" || query.state.data?.status === "processing"
        ? 3000
        : false,
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: createExportTask,
    onSuccess: (task) => {
      rememberTaskId(userId, task.id);
      setTaskId(task.id);
      queryClient.setQueryData<ExportTask>(["data-export", userId, task.id], task);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!taskId) throw new Error("缺少导出任务 ID");
      return cancelExportTask(taskId);
    },
    onSuccess: (task) => {
      queryClient.setQueryData<ExportTask>(queryKey, task);
      toast.success("已取消导出");
    },
  });

  async function download() {
    if (!taskId || isDownloading) return;
    setIsDownloading(true);
    try {
      let task = await getExportTask(taskId);
      if (task.status !== "succeeded") {
        toast.error("导出尚未完成");
        return;
      }
      if (!task.download_url) {
        const unlocked = await onUnlockPrivacy();
        if (!unlocked) return;
        task = await getExportTask(taskId);
      }
      if (!task.download_url) {
        toast.error("暂时无法获取下载链接，请稍后重试");
        return;
      }
      window.location.assign(task.download_url);
    } catch {
      toast.error("暂时无法下载导出文件，请稍后重试");
    } finally {
      setIsDownloading(false);
    }
  }

  return {
    taskId,
    taskQuery,
    create: () => createMutation.mutate(),
    isCreating: createMutation.isPending,
    cancel: () => cancelMutation.mutate(),
    isCancelling: cancelMutation.isPending,
    download,
    isDownloading,
  };
}
